import * as XLSX from "sheetjs-style";
import dayjs from "dayjs";

export function exportHotelsByStatus(hotels: any[], filename: string) {
    if (hotels.length === 0) return;

    const workbook = XLSX.utils.book_new();

    // Simplified categories
    const statusTabs = [
        {key: "before_confirmation", label: "Before Confirmation"},
        {key: "all_confirmed", label: "All Confirmed"},
        {key: "change_requested", label: "Change Requested"},
        {key: "cancelled", label: "Cancelled"},
        {key: "all", label: "All"},
    ];

    // Determine the maximum number of nights across all hotels
    const getNightsCount = (h: any) => {
        if (!h.checkin_date || !h.checkout_date) return 0;
        const checkin = dayjs(h.checkin_date);
        const checkout = dayjs(h.checkout_date);
        return checkout.diff(checkin, "day");
    };
    const maxNights = Math.max(...hotels.map(getNightsCount), 0);

    const formatHotels = (records: any[]): Record<string, any>[] =>
        records.map((h) => {
            // Base row up to "Room Types"
            const row: Record<string, any> = {
                Customer: `${h.customer.first_name} ${h.customer.last_name}`,
                MMID: h.customer.mmid,
                "Check-in": h.checkin_date,
                "Check-out": h.checkout_date,
                Tower: h.tower || "N/A",
                "Room Types": h.room_types?.join(", ") || "N/A",
            };

            const payments: Record<string, string> = h.per_night_payments || {};
            for (let i = 0; i < maxNights; i++) {
                const date = h.checkin_date
                    ? dayjs(h.checkin_date).add(i, "day").format("YYYY-MM-DD")
                    : null;
                row[`Night ${i + 1}`] = date && payments[date] ? payments[date] : "None";
            }

            // Continue with the rest of the columns
            row["Special Requests"] = h.special_requests?.join(", ") || "N/A";
            row["Early Check-in"] = h.early_checkin_time?.slice(0, 5) || "-";
            row["Late Checkout"] = h.late_checkout_time?.slice(0, 5) || "-";
            row["Confirmation #"] = h.confirmation_number || "N/A";
            row["Status"] = h.status || "N/A";
            row["Notes"] = h.notes || "";

            return row;
        });


    statusTabs.forEach(({key, label}) => {
        let filtered: any[] = [];

        if (key === "all") {
            filtered = hotels;
        } else if (key === "all_confirmed") {
            filtered = hotels.filter((h) =>
                ["confirmed", "confirmed_with_changes"].includes(h.status)
            );
        } else {
            filtered = hotels.filter((h) => h.status === key);
        }

        if (filtered.length === 0) return;

        const data = formatHotels(filtered);
        const headers = Object.keys(data[0]);
        const rows = data.map((row) => headers.map((key) => row[key]));
        const sheetData = [headers, ...rows];

        const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

        // Bold headers
        headers.forEach((_, colIndex) => {
            const cellRef = XLSX.utils.encode_cell({r: 0, c: colIndex});
            if (worksheet[cellRef]) {
                worksheet[cellRef].s = {font: {bold: true}};
            }
        });

        // Auto column widths
        worksheet["!cols"] = headers.map((key, index) => {
            const maxLen = Math.max(
                key.length,
                ...rows.map((r) => (r[index] ? r[index].toString().length : 0))
            );
            return {wch: maxLen + 2};
        });

        worksheet["!sheetViews"] = [
            {
                state: "frozen",
                ySplit: 1,
                topLeftCell: "A2",
                activePane: "bottomLeft",
            },
        ];

        XLSX.utils.book_append_sheet(workbook, worksheet, label);
    });

    XLSX.writeFile(workbook, filename, {cellStyles: true});
}
